import React from 'react';
import {
  Cog6ToothIcon,
  ChevronLeftIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { ForecastConfig, SimulationMetadata } from '@/types/forecast';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { ForecastConfiguration } from './ForecastConfiguration';
import { ForecastParameters } from './ForecastParameters';
import { Tooltip } from '@/components/ui';

interface ForecastConfigurationSidebarProps {
  configuration: ForecastConfig;
  onConfigurationChange: (config: ForecastConfig) => void;
  populationSize: number;
  forecastSubsetSize?: number;
  sourceAnalysis: WeibullAnalysisResult | null;
  simulationMetadata: SimulationMetadata | null;
  disabled: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const ForecastConfigurationSidebar: React.FC<ForecastConfigurationSidebarProps> = ({
  configuration,
  onConfigurationChange,
  populationSize,
  forecastSubsetSize,
  sourceAnalysis,
  simulationMetadata,
  disabled,
  isCollapsed: _isCollapsed = false,
  onToggleCollapse,
}) => {
  return (
    <div className="h-full bg-white border-r border-gray-200 flex flex-col w-80">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Cog6ToothIcon className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Configuration</h3>
            <Tooltip 
              content="Configure forecast parameters including time horizon, calculation method, and confidence interval settings." 
              type="info"
            >
              <InformationCircleIcon className="w-4 h-4 text-blue-600 cursor-help" />
            </Tooltip>
          </div>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              title="Close configuration"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
          )}
        </div>
        <p className="text-sm text-gray-600">
          Configure forecast parameters
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 py-4 space-y-6">
          {/* Forecast Configuration */}
          <ForecastConfiguration
            configuration={configuration}
            onConfigurationChange={onConfigurationChange}
            _populationSize={populationSize}
            _forecastSubsetSize={forecastSubsetSize}
            disabled={disabled}
          />

          {/* Divider */}
          <div className="border-t border-gray-200"></div>

          {/* Forecast Parameters */}
          <ForecastParameters
            sourceAnalysis={sourceAnalysis}
            simulationMetadata={simulationMetadata}
          />
        </div>
      </div>
    </div>
  );
};

export default ForecastConfigurationSidebar;